import { h } from 'preact';
import { QueryClientProvider } from 'react-query';
import { useEffect } from 'preact/compat';
import { Provider } from 'react-redux';

import Router from './router';
import loadApp from './app-manifest/app-loader';
import Widgets from './containers/Widgets';
import Tracer from './utils/tracer';
import decodeMinifiedStacktrace from './utils/decode-minified-stacktrace';
import apiEventBus from './api-event-bus';
import createQueryConfig from './queries';
import { API_EVENTS } from './constants/event-bus-events';
import { window } from './env/browser';

function trackDecodedError(type) {
  return encodedError => {
    if (!encodedError) {
      return Promise.resolve();
    }

    return decodeMinifiedStacktrace(encodedError)
      .then(error => {
        console.error(error);
        const context = error.appName ? { appName: error.appName } : {};

        Tracer.addError(error, {
          customError: { ...context, type, decoded: true },
        });
      })
      .catch(() => {
        console.error(new Error('Decoding error process has failed'));
        console.error(encodedError);
        Tracer.addError(encodedError, {
          customError: { type, decoded: false },
        });
      });
  };
}

const globalErrorHandler = event => {
  event.preventDefault();

  return trackDecodedError('error')(event.error);
};

const globalPromiseRejectionHandler = event => {
  event.preventDefault();

  return trackDecodedError('unhandled_promise_rejection')(event.reason);
};

function eventsMap(queryClient, apiClient) {
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const { token } = API_EVENTS;

  return withCallbacks => ({
    [token.load]: withCallbacks(token.success, token.error)(
      function requestToken() {
        return queryClient.fetchQuery(queryConfig.authToken());
      },
    ),
  });
}

function App({ store, ...deps }) {
  useEffect(() => {
    const { unsubscribe } = apiEventBus(
      eventsMap(deps.queryClient, deps.apiClient),
    )(deps.eventBus);

    window.addEventListener(
      'unhandledrejection',
      globalPromiseRejectionHandler,
    );

    window.addEventListener('error', globalErrorHandler);

    return () => {
      unsubscribe();

      window.removeEventListener(
        'unhandledrejection',
        globalPromiseRejectionHandler,
      );

      window.removeEventListener('error', globalErrorHandler);
    };
  }, []);

  return (
    <Provider store={store}>
      <QueryClientProvider client={deps.queryClient}>
        <Router
          deps={{
            loadApp,
            ...deps,
          }}
        />
      </QueryClientProvider>
      <Widgets />
    </Provider>
  );
}

export default App;
